import { useState, useEffect, useCallback } from "react";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";
import { useAuth } from "utilities/auth";
import { useEffectiveCompanyId } from "hooks/useEffectiveCompanyId";

/**
 * useTicketTemplates (SOUND-506) — list of workflow templates (internal-only).
 * Every row carries variantKey / variantLabel / isDefault (SOUND-785).
 * GET /ticket-templates → { data, total }. Thin makeApiRequest wrapper (house
 * pattern; not React Query), CompanyHeader from useEffectiveCompanyId().
 */
export function useTicketTemplates(params = {}, { enabled = true } = {}) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState(null);

  const auth = useAuth();
  const companyId = useEffectiveCompanyId();
  const qs = JSON.stringify(params || {});

  const fetchTemplates = useCallback(async () => {
    // `enabled: false` for non-internal viewers — the endpoint is internal-only
    // and would 403 (SOUND-785: the ticket list/detail resolve variant labels
    // from it for internal users only).
    if (!enabled) {
      setTemplates([]);
      setLoading(false);
      return;
    }
    if (!companyId || !auth?.user) return;
    setLoading(true);
    setError(null);
    try {
      const res = await makeApiRequest(
        "get",
        apiRoutes.listTicketTemplates(JSON.parse(qs)),
        auth.user,
        companyId
      );
      setTemplates(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [enabled, companyId, auth?.user, qs]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  return { templates, loading, error, refetch: fetchTemplates };
}

/**
 * useTicketTemplate(templateId) — a single template's detail. No-ops when
 * templateId is falsy or "new" (the create flow starts from a blank draft).
 */
export function useTicketTemplate(templateId) {
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(!!templateId && templateId !== "new");
  const [error, setError] = useState(null);

  const auth = useAuth();
  const companyId = useEffectiveCompanyId();

  const fetchTemplate = useCallback(async () => {
    if (!templateId || templateId === "new" || !companyId || !auth?.user) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await makeApiRequest(
        "get",
        apiRoutes.getTicketTemplate(templateId),
        auth.user,
        companyId
      );
      setTemplate(res.data?.data || null);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [templateId, companyId, auth?.user]);

  useEffect(() => {
    fetchTemplate();
  }, [fetchTemplate]);

  return { template, loading, error, refetch: fetchTemplate };
}
